import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Download, MessageSquareX, Paperclip, Plus, Trash2, UserCheck } from "lucide-react";
import { API, api, apiError } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { useProject } from "@/pages/project/ProjectLayout";
import { useAuth } from "@/context/AuthContext";
import { useProjectSocket } from "@/hooks/useProjectSocket";
import { IssueDialog } from "@/components/issues/IssueDialog";
import { PurgeIssuesDialog } from "@/components/issues/PurgeIssuesDialog";
import { AssigneePicker } from "@/components/issues/AssigneePicker";
import { ISSUE_STATUSES } from "@/constants/workflow";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PRIORITY_STYLE = {
  Critical: "border-red-500/40 bg-red-500/10 text-red-700",
  High: "border-amber-500/40 bg-amber-500/10 text-amber-700",
  Medium: "border-sky-500/40 bg-sky-500/10 text-sky-700",
  Low: "border-slate-200 bg-slate-100 text-slate-600",
};

export default function IssuesPage() {
  const { projectId, project } = useProject();
  const [data, setData] = useState({ issues: [], counts: {}, total: 0 });
  const [tab, setTab] = useState("All");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [comment, setComment] = useState("");
  const [users, setUsers] = useState([]);
  const { user, capabilities } = useAuth();
  const readOnly = Boolean(capabilities?.is_guest);
  const [purgeMode, setPurgeMode] = useState(null);

  const mine = (issue) => (issue.assignee_ids || []).includes(user?.id);
  const [onlyMine, setOnlyMine] = useState(false);
  const [searchParams] = useSearchParams();
  const focusKey = searchParams.get("issue");

  useEffect(() => {
    api
      .get("/admin/assignable-users")
      .then((res) => setUsers(res.data))
      .catch(() => setUsers([]));
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/issues`);
      setData(res.data);
    } catch (error) {
      toast.error(apiError(error));
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!focusKey || active) return;
    const match = data.issues.find((issue) => issue.issue_key === focusKey);
    if (match)
      api
        .get(`/projects/${projectId}/issues/${match.id}`)
        .then((res) => setActive(res.data))
        .catch((error) => console.debug("[issues] focus lookup failed", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, data.issues]);

  useProjectSocket(projectId, (message) => {
    if (["issue_created", "issue_updated"].includes(message.event)) load();
  });

  const filtered = useMemo(
    () =>
      data.issues
        .filter((i) => tab === "All" || i.status === tab)
        .filter((i) => !onlyMine || mine(i)),
    [data.issues, tab, onlyMine, user?.id]
  );

  const updateIssue = async (issue, patch) => {
    try {
      const { data: fresh } = await api.put(`/projects/${projectId}/issues/${issue.id}`, patch);
      toast.success("Issue updated");
      load();
      if (active?.id === issue.id) setActive(fresh);
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const addComment = async () => {
    try {
      await api.post(`/projects/${projectId}/issues/${active.id}/comments`, { text: comment });
      setComment("");
      const { data: fresh } = await api.get(`/projects/${projectId}/issues/${active.id}`);
      setActive(fresh);
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const tabs = ["All", ...ISSUE_STATUSES];

  return (
    <main
      className="okn-scroll mx-auto w-full max-w-[1500px] flex-1 overflow-y-auto px-4 py-8 sm:px-6"
      data-testid="issues-page"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mono text-[11px] uppercase tracking-[0.3em] text-cyan-600">Tracking</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            Issues & NCR
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {readOnly ? null : (
            <>
          {capabilities?.is_admin ? (
            <>
              <button
                data-testid="purge-comments-button"
                onClick={() => setPurgeMode("comments")}
                className="okn-tool flex items-center gap-1.5 rounded-full border border-amber-500/40 px-4 py-2 text-[10px] uppercase tracking-wider text-amber-700 hover:bg-amber-500/10"
              >
                <MessageSquareX size={13} /> Delete all comments
              </button>
              <button
                data-testid="purge-issues-button"
                onClick={() => setPurgeMode("issues")}
                className="okn-tool flex items-center gap-1.5 rounded-full border border-red-500/40 px-4 py-2 text-[10px] uppercase tracking-wider text-red-700 hover:bg-red-500/10"
              >
                <Trash2 size={13} /> Delete all issues
              </button>
            </>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="issues-export-trigger"
                className="okn-tool flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-4 py-2 text-[10px] uppercase tracking-wider text-emerald-700 hover:bg-emerald-500/25"
              >
                <Download size={13} /> Export Excel
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="border-slate-200 bg-white text-slate-800">
              <DropdownMenuItem asChild data-testid="issues-export-filtered">
                <a
                  href={`${API}/projects/${projectId}/exports/issues?status=${tab}`}
                  className="text-xs"
                >
                  Export Filtered ({filtered.length})
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="issues-export-all">
                <a
                  href={`${API}/projects/${projectId}/exports/issues?scope=all`}
                  className="text-xs"
                >
                  Export All ({data.total})
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            data-testid="new-issue-button"
            onClick={() => setOpen(true)}
            className="rounded-full bg-cyan-500 text-[#04121a] hover:bg-cyan-400"
          >
            <Plus size={15} className="mr-1" /> New issue
          </Button>
            </>
          )}
        </div>
      </div>

      <div className="mt-7 flex flex-wrap gap-1.5" data-testid="issue-tabs">
        {tabs.map((label) => (
          <button
            key={label}
            data-testid={`issue-tab-${label.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => setTab(label)}
            className={`mono rounded-full px-4 py-1.5 text-[10.5px] uppercase tracking-wider transition-colors ${
              tab === label
                ? "bg-cyan-500/18 text-cyan-700"
                : "border border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
          >
            {label} ({label === "All" ? data.total : data.counts[label] || 0})
          </button>
        ))}
        <button
          data-testid="issue-tab-mine"
          onClick={() => setOnlyMine((v) => !v)}
          className={`mono ml-1 flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[10.5px] uppercase tracking-wider transition-colors ${
            onlyMine
              ? "bg-orange-500 text-white"
              : "border border-orange-300 text-orange-700 hover:bg-orange-50"
          }`}
        >
          <UserCheck size={12} /> My issues ({data.issues.filter(mine).length})
        </button>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_380px]">
        <div className="space-y-2" data-testid="issues-list">
          {filtered.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 px-6 py-12 text-center text-sm text-slate-500">
              No issues in this view.
            </p>
          ) : (
            filtered.map((issue) => (
              <button
                key={issue.id}
                data-testid={`issue-row-${issue.issue_key}`}
                onClick={async () => {
                  const { data: fresh } = await api.get(
                    `/projects/${projectId}/issues/${issue.id}`
                  );
                  setActive(fresh);
                }}
                className={`w-full rounded-xl border p-4 text-left transition-colors ${
                  issue.status === "Closed"
                    ? "border-emerald-300 bg-emerald-50 hover:border-emerald-400"
                    : mine(issue)
                      ? "border-orange-300 bg-orange-50/70 hover:border-orange-400"
                      : "border-slate-200 bg-slate-50 opacity-70 hover:opacity-100"
                } ${
                  active?.id === issue.id || issue.issue_key === focusKey
                    ? "ring-2 ring-cyan-500/40"
                    : ""
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`mono text-[10.5px] ${
                      mine(issue) ? "font-semibold text-orange-700" : "text-slate-500"
                    }`}
                  >
                    {issue.issue_key}
                  </span>
                  {mine(issue) ? (
                    <span
                      data-testid={`issue-mine-badge-${issue.issue_key}`}
                      className="mono rounded bg-orange-500 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white"
                    >
                      Mine
                    </span>
                  ) : null}
                  {issue.issue_type === "NCR" ? (
                    <span className="mono rounded bg-red-500/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-red-700">
                      NCR
                    </span>
                  ) : (
                    <span className="mono rounded bg-slate-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-slate-600">
                      {issue.issue_type}
                    </span>
                  )}
                  <span
                    className={`mono rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${
                      PRIORITY_STYLE[issue.priority]
                    }`}
                  >
                    {issue.priority}
                  </span>
                  {issue.status === "Closed" ? (
                    <span
                      data-testid={`issue-closed-badge-${issue.issue_key}`}
                      className="mono ml-auto flex items-center gap-1 text-[9.5px] uppercase tracking-wider text-emerald-800"
                    >
                      <CheckCircle2 size={13} className="text-emerald-700" /> Closed
                    </span>
                  ) : (
                    <span className="mono ml-auto text-[9.5px] uppercase tracking-wider text-slate-500">
                      {issue.status}
                    </span>
                  )}
                </div>
                <p
                  className={`mt-2 text-sm ${
                    mine(issue) ? "font-medium text-slate-900" : "text-slate-600"
                  }`}
                >
                  {issue.title}
                </p>
                <div className="mono mt-2 flex flex-wrap items-center gap-3 text-[10px] text-slate-500">
                  {issue.panel_name ? <span className="text-slate-600">{issue.panel_name}</span> : null}
                  <span data-testid={`issue-created-by-${issue.issue_key}`}>
                    By {issue.created_by}
                  </span>
                  <span data-testid={`issue-assignees-${issue.issue_key}`} className="text-cyan-700">
                    → {issue.assigned_to_name || "Unassigned"}
                  </span>
                  {issue.attachments?.length ? (
                    <span className="flex items-center gap-1">
                      <Paperclip size={10} /> {issue.attachments.length}
                    </span>
                  ) : null}
                  <span className="ml-auto">{new Date(issue.created_at).toLocaleDateString()}</span>
                </div>
              </button>
            ))
          )}
        </div>

        {active ? (
          <aside
            data-testid="issue-detail"
            className="h-fit rounded-xl border border-slate-200 bg-white p-5 lg:sticky lg:top-32"
          >
            <p className="mono text-[10.5px] text-cyan-700">{active.issue_key}</p>
            <h2 className="mt-1.5 font-display text-lg font-medium tracking-tight">{active.title}</h2>
            {active.description ? (
              <p className="mt-3 whitespace-pre-wrap text-[12.5px] leading-relaxed text-slate-700">
                {active.description}
              </p>
            ) : null}

            <div className="mono mt-3 space-y-1 rounded-lg bg-[#EEF2F8] p-3 text-[10.5px] text-slate-600">
              <p data-testid="issue-detail-created-by">
                Created by <b className="text-slate-900">{active.created_by}</b> ·{" "}
                {new Date(active.created_at).toLocaleString()}
              </p>
              <p data-testid="issue-detail-assignees">
                Assigned to{" "}
                <b className="text-cyan-700">{active.assigned_to_name || "Unassigned"}</b>
              </p>
            </div>

            <div className="mt-4">
              <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                Assignees
              </p>
              <AssigneePicker
                testId="issue-detail-assignee-picker"
                users={users}
                value={active.assignee_ids || []}
                onChange={(ids) => updateIssue(active, { assignee_ids: ids })}
              />
            </div>

            <div className="mt-4">
              <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                Status
              </p>
              <Select value={active.status} onValueChange={(v) => updateIssue(active, { status: v })}>
                <SelectTrigger
                  data-testid="issue-status-select"
                  className="h-9 border-slate-200 bg-[#EEF2F8] text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-slate-200 bg-white text-slate-800">
                  {ISSUE_STATUSES.map((status) => (
                    <SelectItem key={status} value={status} className="text-xs">
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {active.attachments?.length ? (
              <div className="mt-4">
                <p className="mono mb-2 text-[10px] uppercase tracking-wider text-slate-500">
                  Photos ({active.attachments.length})
                </p>
                <div className="grid grid-cols-3 gap-2" data-testid="issue-attachments">
                  {active.attachments.map((attachment) => (
                    <a
                      key={attachment.attachment_id}
                      href={`${API}/projects/${projectId}/issues/${active.id}/attachments/${attachment.attachment_id}?variant=original`}
                      target="_blank"
                      rel="noreferrer"
                      className="overflow-hidden rounded-lg border border-slate-200"
                    >
                      {attachment.has_preview ? (
                        <img
                          src={`${API}/projects/${projectId}/issues/${active.id}/attachments/${attachment.attachment_id}?variant=preview`}
                          alt={attachment.file_name}
                          className="h-20 w-full object-cover"
                        />
                      ) : (
                        <div className="grid h-20 place-items-center bg-[#EEF2F8] px-1 text-center">
                          <span className="mono text-[8px] text-slate-600">
                            {attachment.file_name.split(".").pop()?.toUpperCase()}
                          </span>
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-5 border-t border-slate-200 pt-4">
              <p className="mono mb-2 text-[10px] uppercase tracking-wider text-slate-500">
                Comments
              </p>
              <div className="space-y-2">
                {(active.comments || []).map((c) => (
                  <div key={c.comment_id} className="rounded-lg bg-[#EEF2F8] p-3">
                    <p className="text-[11.5px] text-slate-800">{c.text}</p>
                    <p className="mono mt-1 text-[9px] uppercase tracking-wider text-slate-500">
                      {c.created_by} · {new Date(c.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
              <Textarea
                data-testid="issue-comment-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment…"
                className="mt-2 min-h-16 border-slate-200 bg-[#EEF2F8] text-xs"
              />
              <Button
                data-testid="issue-comment-submit"
                disabled={!comment.trim()}
                onClick={addComment}
                className="mt-2 h-8 w-full rounded-full bg-cyan-500 text-[11px] text-[#04121a]"
              >
                Post comment
              </Button>
            </div>
          </aside>
        ) : null}
      </div>

      <IssueDialog
        projectId={projectId}
        panel={null}
        open={open}
        onOpenChange={setOpen}
        onCreated={load}
      />
      <PurgeIssuesDialog
        projectId={projectId}
        projectCode={project?.code || ""}
        total={data.total}
        mode={purgeMode || "issues"}
        open={!!purgeMode}
        onOpenChange={(next) => !next && setPurgeMode(null)}
        onDone={load}
      />
    </main>
  );
}
